import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';

import { Badge as ManagerTeamBadge } from 'components/managerTeam/Badge';

import { Badge } from './Badge';
import { LeagueTrckSlot_so5LeaderboardContender } from './__generated__/index.graphql';

const StyledButtonBase = styled(ButtonBase)`
  max-width: calc(8 * var(--unit));

  &:not(.selected) {
    filter: grayscale(1) brightness(0.6);
  }
`;

type Props = {
  so5LeaderboardContender?: LeagueTrckSlot_so5LeaderboardContender;
  index: number;
  selected: boolean;
  onClick: () => void;
  firstEmptySlot: boolean;
  canCompose: boolean;
  rarity: Rarity;
  completed: boolean;
};

export const LeagueTrckSlot = ({
  index,
  so5LeaderboardContender,
  selected,
  onClick,
  firstEmptySlot,
  canCompose,
  rarity,
  completed,
}: Props) => {
  let badge: ReactNode;

  if (completed) {
    badge = <Badge variant="completed" rarity={rarity} />;
  } else if (so5LeaderboardContender) {
    badge = (
      <ManagerTeamBadge
        leaderboard={so5LeaderboardContender.so5Leaderboard}
        managerTeam={so5LeaderboardContender.managerTeam}
        size={7}
        statusIconSize={4}
      />
    );
  } else if (canCompose && firstEmptySlot) {
    badge = <Badge variant="new" rarity={rarity} />;
  } else {
    badge = <Badge variant="locked" rarity={rarity} />;
  }

  return (
    <StyledButtonBase onClick={onClick} className={classNames({ selected })}>
      <Vertical center>
        {badge}
        <LabelS as="p" bold>
          <Ellipsis>
            {so5LeaderboardContender?.managerTeam?.name || (
              <FormattedMessage
                id="leagueTrck.LeagueTrckSlots.team"
                defaultMessage="Team {number}"
                values={{ number: index + 1 }}
              />
            )}
          </Ellipsis>
        </LabelS>
      </Vertical>
    </StyledButtonBase>
  );
};

LeagueTrckSlot.fragments = {
  so5LeaderboardContender: gql`
    fragment LeagueTrckSlot_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        ...ManagerTeamBadge_so5Leaderboard
      }
      so5Lineup {
        id
        confirmable
      }
      managerTeam {
        id
        hidden
        activeDivisionIconUrl
        name
        ...ManagerTeamBadge_managerTeam
      }
    }
    ${ManagerTeamBadge.fragments.so5Leaderboard}
    ${ManagerTeamBadge.fragments.managerTeam}
  ` as TypedDocumentNode<LeagueTrckSlot_so5LeaderboardContender>,
};
